import React, { useCallback } from 'react';
import './credential-field-editor.scss';

/**
 * One editable row of a credential. Each row is a named field whose value
 * the user is typing. `isSecret` is a UI-only flag controlling whether the
 * value input is masked and whether secret-only helper buttons (Generate /
 * Copy) are exposed on this row. The flag is forwarded to the BE on submit
 * via `secretFieldKeys` so the secret-count gate doesn't have to fall back
 * to the key-name heuristic for user-chosen field names.
 */
export interface CredentialRow {
  /** Stable React key. Different from `name` because the user can rename. */
  rowId: string;
  /** Field name as it will land in the vault (e.g. "username", "pin"). */
  name: string;
  /** Field value. */
  value: string;
  /** UI hint: render the value as type=password and offer secret helpers. */
  isSecret: boolean;
  /**
   * Optional placeholder for the value input. Useful for agent-supplied
   * rows where a hint like "e.g. last 6 digits of phone number" is more
   * helpful than the bare field name.
   */
  placeholder?: string;
  /**
   * Optional human-readable label rendered above the value input. Falls
   * back to `name` when omitted. Agent-supplied fields use this so the
   * label can read "Date of birth" while the vault key stays "dob".
   */
  displayLabel?: string;
}

/**
 * Helper buttons attach inline to the rows. Each helper is opt-in — the
 * caller decides which surfaces (Memories Add, ops worker form, in-task
 * modal) get which affordances. Helpers never mutate row names; they only
 * fill values.
 */
export interface CredentialFieldHelpers {
  /**
   * Render a "Generate" button on every secret row. Wired into the
   * caller's password-generator. The callback resolves with the generated
   * value so the editor can write it into the row's `value`.
   */
  generatePassword?: () => Promise<string | null>;
  /**
   * Render a "Use user's email" button on every non-secret row whose name
   * looks identifier-shaped (username / email / login / etc.). Used only
   * by the ops worker form today.
   */
  useUserEmail?: () => Promise<string | null>;
  /**
   * Render a "Copy" button on every secret row that has a non-empty value.
   * Mostly useful for ops — they need to paste the generated password into
   * the third-party site they're signing up to.
   */
  copyValue?: boolean;
}

interface CredentialFieldEditorProps {
  rows: CredentialRow[];
  onChange: (next: CredentialRow[]) => void;
  /**
   * Field names that cannot be renamed or removed. Used when the caller is
   * a structured artifact whose downstream consumer expects a specific
   * shape (e.g. the agent asked for "username + password + dob" and the
   * task prompt later references those keys by name).
   */
  lockedKeys?: string[];
  /**
   * Whole-editor disable, e.g. while the parent form is submitting.
   */
  disabled?: boolean;
  /**
   * Opt-in helper buttons. See `CredentialFieldHelpers`.
   */
  helpers?: CredentialFieldHelpers;
  /**
   * Show the "+ Add field" button. Defaults to true. The agent-driven
   * paths (response artifact, in-task question) can set it to false so
   * the user can't extend the agent's requested shape — useful when the
   * downstream consumer is a tightly-typed contract.
   */
  allowAdd?: boolean;
  /**
   * Maximum number of rows. Defaults to 12 — generous enough for the most
   * baroque credential (banking with multiple security questions) and
   * tight enough that the modal stays scrollable on a laptop screen.
   */
  maxRows?: number;
}

const DEFAULT_MAX_ROWS = 12;

/**
 * Loose identifier-shaped heuristic used by the "Use user's email" helper
 * to decide whether to expose the button on a given row. Lifted from the
 * BE classifier (lib/credentialMatching.ts) so the FE and BE agree on
 * which keys are account-identifier-shaped.
 */
const IDENTIFIER_NAME_PATTERN =
  /^(username|user|user_name|userid|user_id|email|email_address|login|account|account_name|handle)$/i;

const makeRowId = (): string =>
  // crypto.randomUUID is available in all evergreen browsers we ship to.
  // Fall back to a Math.random() id when running in an older sandbox
  // (jest occasionally lacks it) so the editor never throws at construction.
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `row-${Math.random().toString(36).slice(2)}-${Date.now()}`;

/**
 * Convenience factory used by callers seeding initial state. Keeps the
 * `rowId` generation centralised so we don't sprinkle uuid calls all over
 * the codebase.
 */
export function makeCredentialRow(
  init: Omit<CredentialRow, 'rowId'>,
): CredentialRow {
  return { rowId: makeRowId(), ...init };
}

/**
 * Loose secret-name heuristic for pre-seeding the editor from existing
 * vault contents. Mirrors the BE `SECRET_KEY_SUBSTRINGS` /
 * `SECRET_KEY_CAMEL_SUFFIXES` lists in `lib/credentialMatching.ts` — the
 * two sides should agree on which key names look secret-shaped so the
 * round-trip (vault → edit → save) preserves the original `isSecret`
 * setting without ceremony from the user.
 */
const SECRET_NAME_SUBSTRINGS = [
  'password',
  'pin',
  'passphrase',
  'secret',
  'token',
  'cvv',
  'cvc',
  'securityanswer',
  'security_answer',
];
const SECRET_NAME_CAMEL_SUFFIXES = [
  'Key',
  'Secret',
  'Token',
  'Password',
  'Pin',
  'Passphrase',
];
export function inferIsSecretFromKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (SECRET_NAME_SUBSTRINGS.some((s) => lower.includes(s))) return true;
  return SECRET_NAME_CAMEL_SUFFIXES.some((s) => key.endsWith(s));
}

const SECRET_FIELD_NAMES = new Set(['password', 'cvv']);

/**
 * Simple name-based check used by the credential editor UI to auto-derive
 * whether a field should be masked. Only exact lowercase matches qualify.
 */
export function isSecretFieldName(name: string): boolean {
  return SECRET_FIELD_NAMES.has(name.trim().toLowerCase());
}

/**
 * Predicate the caller uses to decide whether a submit gate is satisfied.
 * Mirrors the BE secret-count gate in `storeAndVaultCredential` — at
 * least one row must be marked secret AND carry a non-empty value.
 *
 * Lives in this module so all six surfaces apply the same rule without
 * each one re-implementing it.
 */
export function hasAtLeastOneSecretValue(rows: CredentialRow[]): boolean {
  return rows.some((r) => r.isSecret && r.value.trim().length > 0);
}

/**
 * Collapse the row list into the `{ fields, secretFieldKeys }` payload
 * the BE accepts. Trims values, drops rows with empty names, and dedupes
 * keys (last-write-wins — the editor prevents duplicates at the UI level
 * via `rowsHaveDuplicateNames`, but a defensive collapse keeps the wire
 * shape valid even if the FE state is malformed).
 */
export function collapseRowsForSubmit(rows: CredentialRow[]): {
  fields: Record<string, string>;
  secretFieldKeys: string[];
} {
  const fields: Record<string, string> = {};
  const secrets: string[] = [];
  for (const r of rows) {
    const name = r.name.trim();
    if (!name) continue;
    fields[name] = r.value.trim();
    if (r.isSecret) secrets.push(name);
  }
  return { fields, secretFieldKeys: secrets };
}

/**
 * Check for duplicate non-empty field names. Returned as a Set so the
 * editor can flag the offending rows inline. We accept multiple empty
 * names (they don't survive `collapseRowsForSubmit`).
 */
export function rowsHaveDuplicateNames(rows: CredentialRow[]): Set<string> {
  const seen = new Set<string>();
  const dups = new Set<string>();
  for (const r of rows) {
    const name = r.name.trim();
    if (!name) continue;
    if (seen.has(name)) dups.add(name);
    else seen.add(name);
  }
  return dups;
}

/** What's missing from an incomplete row. */
export type RowIncompleteness = 'name' | 'value' | 'both';

/**
 * Find every row that's not fully populated. A row is "incomplete" when
 * either its name or its value (after trim) is empty. The submit gate in
 * each calling form uses this so the user can't save partially-filled
 * fields:
 *   - Nameless rows with a value get silently dropped by
 *     `collapseRowsForSubmit` (the user types a value and thinks it
 *     was saved when it wasn't).
 *   - Named rows with no value clutter the vault with empty entries.
 *   - Wholly empty rows are usually a forgotten "+ Add field" click.
 *
 * Returned as a map from rowId to which side is missing so the editor can
 * tailor the inline hint.
 */
export function rowIncompletenessByRowId(
  rows: CredentialRow[],
): Map<string, RowIncompleteness> {
  const out = new Map<string, RowIncompleteness>();
  for (const r of rows) {
    const hasName = r.name.trim().length > 0;
    const hasValue = r.value.trim().length > 0;
    if (hasName && hasValue) continue;
    if (!hasName && !hasValue) out.set(r.rowId, 'both');
    else if (!hasName) out.set(r.rowId, 'name');
    else out.set(r.rowId, 'value');
  }
  return out;
}

/**
 * Unified credential field editor.
 *
 * Renders a list of `{name, value, isSecret}` rows backed by the caller's
 * state. Used by every surface where a user or ops worker types credential
 * fields (Memories Add / Edit, in-task modal, response artifact, ops
 * worker creation form). The look-and-feel is identical everywhere so the
 * user's mental model is consistent regardless of how the form was
 * launched.
 *
 * Responsibilities:
 *  - Row name / value / secret-toggle inputs
 *  - Add / remove buttons (with locked-key respect)
 *  - Per-row secret helpers (Generate / Copy / Use user's email)
 *  - Visual indication of duplicate field names
 *
 * NOT this component's job:
 *  - Submit gating — caller checks `hasAtLeastOneSecretValue(rows)`
 *  - The label / site / purpose metadata — those are top-level inputs in
 *    each calling modal, not per-row data
 *  - Network calls — helpers are passed in as callbacks, so this file
 *    stays dependency-free and testable in isolation
 */
const CredentialFieldEditor: React.FC<CredentialFieldEditorProps> = ({
  rows,
  onChange,
  lockedKeys,
  disabled,
  helpers,
  allowAdd = true,
  maxRows = DEFAULT_MAX_ROWS,
}) => {
  const lockedSet = React.useMemo(
    () => new Set(lockedKeys ?? []),
    [lockedKeys],
  );
  const duplicateNames = React.useMemo(
    () => rowsHaveDuplicateNames(rows),
    [rows],
  );
  const incompletenessByRowId = React.useMemo(
    () => rowIncompletenessByRowId(rows),
    [rows],
  );

  // Track per-row "visible" state for secret values. Stored by rowId so
  // toggling a row's visibility survives the row being reordered (today
  // we don't expose reordering, but the data model accommodates it).
  const [visibleRows, setVisibleRows] = React.useState<Record<string, boolean>>(
    {},
  );

  const setRow = useCallback(
    (rowId: string, mutator: (r: CredentialRow) => CredentialRow) => {
      onChange(rows.map((r) => (r.rowId === rowId ? mutator(r) : r)));
    },
    [rows, onChange],
  );

  const removeRow = useCallback(
    (rowId: string) => {
      onChange(rows.filter((r) => r.rowId !== rowId));
    },
    [rows, onChange],
  );

  const addRow = useCallback(() => {
    if (rows.length >= maxRows) return;
    onChange([
      ...rows,
      makeCredentialRow({ name: '', value: '', isSecret: false }),
    ]);
  }, [rows, onChange, maxRows]);

  const toggleVisible = useCallback((rowId: string) => {
    setVisibleRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const onGenerate = useCallback(
    async (rowId: string) => {
      if (!helpers?.generatePassword) return;
      const value = await helpers.generatePassword();
      if (value == null) return;
      setRow(rowId, (r) => ({ ...r, value }));
      // Reveal the generated value so the user can verify it before
      // saving. Otherwise they'd be saving a black box.
      setVisibleRows((prev) => ({ ...prev, [rowId]: true }));
    },
    [helpers, setRow],
  );

  const onUseUserEmail = useCallback(
    async (rowId: string) => {
      if (!helpers?.useUserEmail) return;
      const value = await helpers.useUserEmail();
      if (value == null) return;
      setRow(rowId, (r) => ({ ...r, value }));
    },
    [helpers, setRow],
  );

  const onCopy = useCallback(async (value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard API can fail in non-secure contexts (e.g. unit tests).
      // Silent — the user can manually select the visible value as a
      // fallback when the toggle is in show mode.
    }
  }, []);

  return (
    <div className='credential-field-editor'>
      <div className='cfe-rows'>
        {rows.map((row) => {
          const locked = lockedSet.has(row.name);
          const visible = !!visibleRows[row.rowId];
          const nameHasDup =
            row.name.trim().length > 0 && duplicateNames.has(row.name.trim());
          // We deliberately do NOT surface a per-row error for incomplete
          // rows (missing name or value). The disabled Save button is a
          // sufficient signal that something is missing, and the empty
          // input speaks for itself. Showing red borders before the user
          // has typed anything punishes them for normal in-progress
          // editing. The submit gate in the parent form still uses
          // `rowIncompletenessByRowId` to keep Save disabled until every
          // row has both a name and a value.
          const looksIdentifier =
            !row.isSecret && IDENTIFIER_NAME_PATTERN.test(row.name.trim());
          // No per-row column labels. The field-name input doubles as
          // its own label — "username" / "password" / etc. is enough
          // context to identify the field, and the placeholder hints
          // at what other field names are conventional.
          const namePlaceholder = row.isSecret
            ? 'e.g. password, pin'
            : 'e.g. username, email, account number';
          return (
            <div
              key={row.rowId}
              className={`cfe-row ${locked ? 'cfe-row--locked' : ''}`}
            >
              <div className='cfe-row-name-col'>
                <input
                  className={`cfe-row-name-input ${nameHasDup ? 'cfe-row-name-input--dup' : ''}`}
                  type='text'
                  value={row.name}
                  placeholder={namePlaceholder}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setRow(row.rowId, (r) => ({
                      ...r,
                      name: newName,
                      isSecret: isSecretFieldName(newName),
                    }));
                  }}
                  disabled={disabled || locked}
                  spellCheck={false}
                  autoCapitalize='none'
                  aria-label={row.displayLabel ?? 'Field name'}
                />
                {nameHasDup && (
                  <div className='cfe-row-name-error'>
                    Duplicate field name — rename one.
                  </div>
                )}
              </div>

              <div className='cfe-row-value-col'>
                <div className='cfe-row-value-input-wrap'>
                  <div
                    className={`cfe-row-value-inner ${row.isSecret ? 'cfe-row-value-inner--secret' : ''}`}
                  >
                    <input
                      className='cfe-row-value-input'
                      type={row.isSecret && !visible ? 'password' : 'text'}
                      value={row.value}
                      placeholder={row.placeholder || 'Value'}
                      onChange={(e) =>
                        setRow(row.rowId, (r) => ({
                          ...r,
                          value: e.target.value,
                        }))
                      }
                      disabled={disabled}
                      autoComplete={row.isSecret ? 'new-password' : 'off'}
                      aria-label={`${row.displayLabel ?? row.name ?? 'Field'} value`}
                    />
                    {row.isSecret && (
                      <button
                        type='button'
                        className='cfe-row-toggle-visibility'
                        onClick={() => toggleVisible(row.rowId)}
                        disabled={disabled}
                        tabIndex={-1}
                      >
                        {visible ? 'Hide' : 'View'}
                      </button>
                    )}
                  </div>
                  {row.isSecret && helpers?.generatePassword && (
                    <button
                      type='button'
                      className='cfe-row-inline-btn'
                      onClick={() => onGenerate(row.rowId)}
                      disabled={disabled}
                    >
                      Generate
                    </button>
                  )}
                  {row.isSecret && helpers?.copyValue && row.value && (
                    <button
                      type='button'
                      className='cfe-row-inline-btn'
                      onClick={() => onCopy(row.value)}
                      disabled={disabled}
                    >
                      Copy
                    </button>
                  )}
                  {looksIdentifier && helpers?.useUserEmail && (
                    <button
                      type='button'
                      className='cfe-row-inline-btn'
                      onClick={() => onUseUserEmail(row.rowId)}
                      disabled={disabled}
                    >
                      Use user&rsquo;s email
                    </button>
                  )}
                </div>
              </div>

              <div className='cfe-row-actions-col'>
                {!locked && (
                  <button
                    type='button'
                    className='cfe-row-remove-btn'
                    onClick={() => removeRow(row.rowId)}
                    disabled={disabled}
                    aria-label='Remove this field'
                    title='Remove this field'
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {allowAdd && rows.length < maxRows && (
        <button
          type='button'
          className='cfe-add-row-btn'
          onClick={addRow}
          disabled={disabled}
        >
          + Add field
        </button>
      )}
    </div>
  );
};

export default CredentialFieldEditor;
